import { useMemo, useState } from 'react';
import { useQuery } from '@apollo/client';
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

import { ASK_ADDY_USAGE_QUERY } from '../../queries/askAddyUsage';
import { currency } from '../../utils';
import LoadingSpinner from '../../components/LoadingSpinner';

interface UsageRow {
  key: string;
  label: string | null;
  generations: number;
  chatMessages: number;
  tokensIn: number;
  tokensOut: number;
  costUsd: number;
  costSource: string;
}

interface UsageTotals {
  conversations: number;
  generations: number;
  chatMessages: number;
  tokensIn: number;
  tokensOut: number;
  costUsd: number;
  untrackedMessages: number;
}

interface UsageReport {
  from: string | null;
  to: string | null;
  groupBy: string;
  totals: UsageTotals;
  rows: UsageRow[];
}

type BreakdownDim = 'model' | 'promptType';

const PROMPT_TYPE_LABELS: Record<string, string> = {
  talking_points: 'Talking Points',
  client_email: 'Client Email',
  exec_summary: 'Exec Summary',
  audience_recs: 'Audience Recs',
  cog_sci_playbook: 'Cog Sci Playbook',
};

// Shorten the long GLOBAL inference-profile id to the human model name.
const modelLabel = (id: string) => {
  if (/sonnet/i.test(id)) return 'Sonnet 4.5';
  if (/haiku/i.test(id)) return 'Haiku 4.5';
  return id;
};

const num = (n: number) => (n ?? 0).toLocaleString();
const cost = (n: number) => currency(n ?? 0, 2, 4);

const startOfDayIso = (d: Date) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.toISOString();
};
const endOfDayIso = (d: Date) => {
  const x = new Date(d);
  x.setHours(23, 59, 59, 999);
  return x.toISOString();
};

// Highlight rows whose cost isn't fully measured so admins know which numbers
// to trust.
function CostSourceBadge({ source }: { source: string }) {
  if (source === 'measured') return null;
  const styles: Record<string, string> = {
    untracked: 'bg-gray-200 text-gray-700',
    estimate: 'bg-yellow-100 text-yellow-800',
    mixed: 'bg-yellow-100 text-yellow-800',
  };
  return (
    <span
      className={`ml-2 rounded px-1.5 py-0.5 text-xs font-medium ${
        styles[source] || 'bg-gray-200 text-gray-700'
      }`}
      title="Some messages in this row have no measured cost"
    >
      {source}
    </span>
  );
}

function SummaryCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-half-small border border-gray-200 bg-white p-4">
      <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        {label}
      </div>
      <div className="mt-1 text-2xl font-semibold text-navy">{value}</div>
    </div>
  );
}

export default function AskAddyUsagePage() {
  // Default window: last 30 days.
  const [startDate, setStartDate] = useState<Date>(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return d;
  });
  const [endDate, setEndDate] = useState<Date>(() => new Date());
  const [breakdownDim, setBreakdownDim] = useState<BreakdownDim>('model');
  const [selectedUser, setSelectedUser] = useState<{ key: string; label: string } | null>(
    null
  );

  const window = useMemo(
    () => ({ from: startOfDayIso(startDate), to: endOfDayIso(endDate) }),
    [startDate, endDate]
  );

  // Per-user report drives the summary cards + the main table.
  const {
    data: userData,
    loading: userLoading,
    error: userError,
  } = useQuery<{ askAddyUsage: UsageReport }>(ASK_ADDY_USAGE_QUERY, {
    variables: { from: window.from, to: window.to, groupBy: 'user' },
    fetchPolicy: 'cache-and-network',
  });

  // Breakdown report (by model / prompt type), optionally scoped to one user.
  const { data: breakdownData, loading: breakdownLoading } = useQuery<{
    askAddyUsage: UsageReport;
  }>(ASK_ADDY_USAGE_QUERY, {
    variables: {
      from: window.from,
      to: window.to,
      groupBy: breakdownDim,
      userId: selectedUser?.key,
    },
    fetchPolicy: 'cache-and-network',
  });

  const report = userData?.askAddyUsage;
  const totals = report?.totals;
  const userRows = report?.rows || [];
  const breakdownRows = breakdownData?.askAddyUsage?.rows || [];

  const dateInputClass =
    'border border-gray-300 rounded px-3 py-2 text-sm w-36 focus:ring-0 focus:border-gray-400';

  return (
    <div className="h-full w-full overflow-auto p-4">
      <div className="mb-4 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h3 className="text-lg font-semibold">Ask Addy — Usage &amp; Cost</h3>
          <p className="text-sm text-gray-500">
            Bedrock / Claude token consumption and USD spend. Cost is derived from
            measured tokens; rows flagged{' '}
            <span className="font-medium">untracked</span> /{' '}
            <span className="font-medium">mixed</span> include messages with no
            measured cost.
          </p>
        </div>
        <div className="flex items-end gap-3">
          <label className="flex flex-col text-xs font-medium text-gray-600">
            From
            <DatePicker
              selected={startDate}
              onChange={(date) => date && setStartDate(date)}
              selectsStart
              startDate={startDate}
              endDate={endDate}
              maxDate={endDate}
              dateFormat="MM/dd/yyyy"
              className={dateInputClass}
            />
          </label>
          <label className="flex flex-col text-xs font-medium text-gray-600">
            To
            <DatePicker
              selected={endDate}
              onChange={(date) => date && setEndDate(date)}
              selectsEnd
              startDate={startDate}
              endDate={endDate}
              minDate={startDate}
              dateFormat="MM/dd/yyyy"
              className={dateInputClass}
            />
          </label>
        </div>
      </div>

      {userError && (
        <div className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {userError.message}
        </div>
      )}

      {userLoading && !report ? (
        <LoadingSpinner message="Loading usage…" className="mt-10" />
      ) : (
        <>
          {/* Summary cards */}
          <div className="mb-6 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-6">
            <SummaryCard label="Total spend" value={cost(totals?.costUsd || 0)} />
            <SummaryCard label="Conversations" value={num(totals?.conversations || 0)} />
            <SummaryCard label="Generations" value={num(totals?.generations || 0)} />
            <SummaryCard label="Chat messages" value={num(totals?.chatMessages || 0)} />
            <SummaryCard
              label="Total tokens"
              value={num((totals?.tokensIn || 0) + (totals?.tokensOut || 0))}
            />
            <SummaryCard label="Untracked msgs" value={num(totals?.untrackedMessages || 0)} />
          </div>

          {/* Per-user table */}
          <div className="mb-2 flex items-center justify-between">
            <h4 className="font-medium">By user</h4>
            <span className="text-xs text-gray-500">Click a row to filter the breakdown</span>
          </div>
          <div className="overflow-x-auto rounded border">
            <table className="min-w-full table-auto text-left text-sm">
              <thead className="border-b bg-gray-100 font-semibold">
                <tr>
                  <th className="px-4 py-2">User</th>
                  <th className="px-4 py-2 text-right">Generations</th>
                  <th className="px-4 py-2 text-right">Chat msgs</th>
                  <th className="px-4 py-2 text-right">Tokens in</th>
                  <th className="px-4 py-2 text-right">Tokens out</th>
                  <th className="px-4 py-2 text-right">Cost (USD)</th>
                </tr>
              </thead>
              <tbody>
                {userRows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                      No usage in this window.
                    </td>
                  </tr>
                )}
                {userRows.map((row) => {
                  const isSelected = selectedUser?.key === row.key;
                  return (
                    <tr
                      key={row.key}
                      onClick={() =>
                        setSelectedUser(
                          isSelected ? null : { key: row.key, label: row.label || row.key }
                        )
                      }
                      className={`cursor-pointer border-b hover:bg-gray-50 ${
                        isSelected ? 'bg-primary/10' : ''
                      }`}
                    >
                      <td className="px-4 py-2">
                        {row.label || row.key}
                        <CostSourceBadge source={row.costSource} />
                      </td>
                      <td className="px-4 py-2 text-right">{num(row.generations)}</td>
                      <td className="px-4 py-2 text-right">{num(row.chatMessages)}</td>
                      <td className="px-4 py-2 text-right">{num(row.tokensIn)}</td>
                      <td className="px-4 py-2 text-right">{num(row.tokensOut)}</td>
                      <td className="px-4 py-2 text-right font-medium">{cost(row.costUsd)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Breakdown */}
          <div className="mt-8 mb-2 flex flex-wrap items-center justify-between gap-2">
            <h4 className="font-medium">
              Breakdown
              {selectedUser && (
                <span className="ml-2 text-sm font-normal text-gray-500">
                  for {selectedUser.label}{' '}
                  <button
                    type="button"
                    onClick={() => setSelectedUser(null)}
                    className="text-primary underline"
                  >
                    clear
                  </button>
                </span>
              )}
            </h4>
            <div className="flex overflow-hidden rounded border text-sm">
              <button
                type="button"
                onClick={() => setBreakdownDim('model')}
                className={`px-3 py-1.5 ${
                  breakdownDim === 'model' ? 'bg-primary text-white' : 'bg-white text-gray-700'
                }`}
              >
                By model
              </button>
              <button
                type="button"
                onClick={() => setBreakdownDim('promptType')}
                className={`border-l px-3 py-1.5 ${
                  breakdownDim === 'promptType'
                    ? 'bg-primary text-white'
                    : 'bg-white text-gray-700'
                }`}
              >
                By prompt type
              </button>
            </div>
          </div>
          <div className="overflow-x-auto rounded border">
            <table className="min-w-full table-auto text-left text-sm">
              <thead className="border-b bg-gray-100 font-semibold">
                <tr>
                  <th className="px-4 py-2">
                    {breakdownDim === 'model' ? 'Model' : 'Prompt type'}
                  </th>
                  <th className="px-4 py-2 text-right">Generations</th>
                  <th className="px-4 py-2 text-right">Chat msgs</th>
                  <th className="px-4 py-2 text-right">Tokens in</th>
                  <th className="px-4 py-2 text-right">Tokens out</th>
                  <th className="px-4 py-2 text-right">Cost (USD)</th>
                </tr>
              </thead>
              <tbody>
                {breakdownLoading && breakdownRows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                      Loading…
                    </td>
                  </tr>
                )}
                {!breakdownLoading && breakdownRows.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                      No usage in this window.
                    </td>
                  </tr>
                )}
                {breakdownRows.map((row) => (
                  <tr key={row.key} className="border-b">
                    <td className="px-4 py-2">
                      {breakdownDim === 'model'
                        ? modelLabel(row.key)
                        : PROMPT_TYPE_LABELS[row.key] || row.key}
                      <CostSourceBadge source={row.costSource} />
                    </td>
                    <td className="px-4 py-2 text-right">{num(row.generations)}</td>
                    <td className="px-4 py-2 text-right">{num(row.chatMessages)}</td>
                    <td className="px-4 py-2 text-right">{num(row.tokensIn)}</td>
                    <td className="px-4 py-2 text-right">{num(row.tokensOut)}</td>
                    <td className="px-4 py-2 text-right font-medium">{cost(row.costUsd)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
    </div>
  );
}
